// path: frontend/src/components/pages/admin/models/AddModelModal.jsx
import React, { useState, useEffect } from "react";
import {
  getAllModelTypes,
  getAllSources,
  getAllModels,
  getOpenRouterData,
} from "../../../../api/apiFunction/modelServices";
import { Loader2, Search, ChevronDown } from "lucide-react";

const AddModelModal = ({ isOpen, onSave, onClose }) => {
  const [form, setForm] = useState({
    name: "",
    title: "",
    provider: "",
    type: "free",
    active: true,
    modelType: "",
    source: "",
    llmClass: "low",
    support: { imageIn: false, imageOut: false, pdf: false, search: false },
    isAuto: false,
    enhancePrompt: false,
  });

  const [loading, setLoading] = useState(false);
  const [modelTypeOptions, setModelTypeOptions] = useState([]);
  const [sourceOptions, setSourceOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);
  const [openRouterModels, setOpenRouterModels] = useState([]);
  const [openRouterProviders, setOpenRouterProviders] = useState([]);
  const [modelSearchQuery, setModelSearchQuery] = useState("");
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);
  // Names of models that already exist in our DB (so we can disable them in the dropdown)
  const [existingModelNames, setExistingModelNames] = useState([]);

  useEffect(() => {
    if (!isOpen) return;
    const fetchDropdownData = async () => {
      setLoading(true);
      try {
        const [typesRes, sourcesRes, modelsRes, openRouterRes] =
          await Promise.all([
            getAllModelTypes().catch(() => ({ data: [] })),
            getAllSources().catch(() => ({ data: [] })),
            getAllModels().catch(() => ({ data: [] })),
            getOpenRouterData().catch((err) => {
              return { data: { success: false, providers: [], models: [] } };
            }),
          ]);

        // Model Types
        const typeFromSupports = Array.from(
          new Set(
            (typesRes.data || []).flatMap((t) =>
              Array.isArray(t.supports) ? t.supports : []
            )
          )
        );
        const typeFromTypeField = Array.from(
          new Set((typesRes.data || []).map((t) => t.type).filter(Boolean))
        );
        const mergedTypes = Array.from(
          new Set([...typeFromSupports, ...typeFromTypeField])
        );
        setModelTypeOptions(mergedTypes);

        if (mergedTypes.length && !form.modelType) {
          setForm((prev) => ({ ...prev, modelType: mergedTypes[0] }));
        }

        // Sources (capitalize first letter)
        const sources = (sourcesRes.data || [])
          .map((s) => s.name)
          .filter(Boolean)
          .map((s) => s.charAt(0).toUpperCase() + s.slice(1));

        setSourceOptions(sources);
        if (sources.length && !form.source) {
          setForm((prev) => ({ ...prev, source: sources[0] }));
        }

        // Capture existing model names from our DB (used to disable already-added models)
        const existingNames = Array.from(
          new Set((modelsRes.data || []).map((m) => m.name).filter(Boolean))
        );
        setExistingModelNames(existingNames);

        // OpenRouter data - handle response structure
        const openRouterData = openRouterRes?.data || {};
        const hasOpenRouterData =
          openRouterData.success === true ||
          (openRouterData.models && openRouterData.models.length > 0);

        if (hasOpenRouterData) {
          const providers = openRouterData.providers || [];
          const models = openRouterData.models || [];

          setOpenRouterProviders(providers);
          setOpenRouterModels(models);

          // Merge OpenRouter providers with existing providers
          const existingProviders = Array.from(
            new Set(
              (modelsRes.data || []).map((m) => m.provider).filter(Boolean)
            )
          );
          const allProviders = Array.from(
            new Set([...existingProviders, ...providers])
          ).sort();
          setProviderOptions(allProviders);
        } else {
          // Fallback to existing providers if OpenRouter fails
          const providers = Array.from(
            new Set(
              (modelsRes.data || []).map((m) => m.provider).filter(Boolean)
            )
          );
          setProviderOptions(providers);
          setOpenRouterModels([]);
          setOpenRouterProviders([]);
        }
      } catch (_) {
        // leave defaults on error
      } finally {
        setLoading(false);
      }
    };
    fetchDropdownData();
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isModelDropdownOpen &&
        !event.target.closest(".model-dropdown-container")
      ) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isModelDropdownOpen]);

  if (!isOpen) return null;

  // Filter models based on search query
  const filteredModels = openRouterModels.filter((model) => {
    const searchLower = modelSearchQuery.toLowerCase();
    return (
      model.id?.toLowerCase().includes(searchLower) ||
      model.name?.toLowerCase().includes(searchLower) ||
      model.provider?.toLowerCase().includes(searchLower)
    );
  });

  // Handle model selection from searchable dropdown
  const handleModelSelect = (modelId) => {
    const selectedModel = openRouterModels.find((m) => m.id === modelId);
    if (selectedModel) {
      // Console log model capabilities

      // Determine support capabilities from model architecture
      const inputModalities =
        selectedModel.architecture?.input_modalities || [];
      const outputModalities =
        selectedModel.architecture?.output_modalities || [];
      const modality = selectedModel.architecture?.modality || "";
      const contextLength = selectedModel.contextLength || 0;
      const pricing = selectedModel.pricing || {};

      // Auto-detect support based on model capabilities
      // imageIn: Model can accept images as input
      const imageIn = inputModalities.some(
        (mod) =>
          mod.toLowerCase().includes("image") ||
          mod.toLowerCase().includes("vision")
      );

      // imageOut: Model can generate images as output
      const imageOut = outputModalities.some((mod) =>
        mod.toLowerCase().includes("image")
      );

      // PDF support: Text models with sufficient context length can typically process PDFs
      const pdf = inputModalities.includes("text") && contextLength >= 4000;

      // Search: Check if web_search pricing exists (indicates search capability)
      const search =
        pricing.web_search !== undefined && parseFloat(pricing.web_search) >= 0;

      setForm((prev) => ({
        ...prev,
        name: selectedModel.id, // Store model ID (e.g., "openai/gpt-4")
        title: selectedModel.name || "", // Auto-fill title
        provider: selectedModel.provider || "", // Auto-fill provider
        support: {
          imageIn: imageIn,
          imageOut: imageOut,
          pdf: pdf,
          search: search,
        },
        // Default enhancePrompt to true for image models that support images, otherwise false
        enhancePrompt: (prev.modelType || "").toLowerCase() === "image",
      }));
      setModelSearchQuery("");
      setIsModelDropdownOpen(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith("support.")) {
      const key = name.split(".")[1];
      setForm((prev) => ({
        ...prev,
        support: {
          ...prev.support,
          [key]: type === "checkbox" ? checked : value,
        },
      }));
    } else if (name === "active") {
      setForm((prev) => ({ ...prev, active: value === "true" }));
    } else if (type === "checkbox") {
      setForm((prev) => ({ ...prev, [name]: checked }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name) {
      alert("Please select a model from the Name dropdown");
      return;
    }
    onSave(form);
    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setForm({
      name: "",
      title: "",
      provider: "",
      type: "free",
      active: true,
      modelType: "",
      source: "",
      llmClass: "low",
      support: { imageIn: false, imageOut: false, pdf: false, search: false },
      isAuto: false,
    });
    setModelSearchQuery("");
    setIsModelDropdownOpen(false);
    setUseManualEntry(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-bg-60 rounded-xl shadow-lg p-6 w-full max-w-3xl relative">
        <button
          className="absolute top-2 right-2 text-fg-60 hover:text-fg-50 text-2xl"
          onClick={handleClose}
        >
          &times;
        </button>
        <h2 className="text-lg font-bold mb-6">Add New Model</h2>

        {loading ? (
          <div className="flex justify-center items-center py-10">
            <Loader2 className="animate-spin text-ac-02 w-6 h-6" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div className="relative">
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-fg-60">
                  Name *
                </label>
                {openRouterModels.length > 0 && !loading && (
                  <button
                    type="button"
                    onClick={() => {
                      setUseManualEntry(!useManualEntry);
                      if (!useManualEntry) {
                        setForm((prev) => ({ ...prev, name: "" }));
                      }
                    }}
                    className="text-xs text-ac-02 hover:text-ac-01"
                  >
                    {useManualEntry ? "Use OpenRouter" : "Enter manually"}
                  </button>
                )}
              </div>
              {loading ? (
                <div className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-60 text-sm flex items-center gap-2">
                  <Loader2 className="animate-spin w-4 h-4" />
                  <span>Loading models...</span>
                </div>
              ) : useManualEntry || openRouterModels.length === 0 ? (
                <div className="space-y-1">
                  <input
                    className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                    placeholder="e.g., qwen/qwen-image-edit or owner/model-name"
                  />
                  <p className="text-xs text-fg-60">
                    {openRouterModels.length > 0
                      ? "Enter Replicate or other model ID manually (e.g., qwen/qwen-image-edit)"
                      : "OpenRouter models not available. Enter model ID manually."}
                  </p>
                </div>
              ) : (
                <div className="relative model-dropdown-container">
                  {/* Selected value display / Search input */}
                  <div
                    className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-ac-02 text-sm cursor-pointer flex items-center justify-between"
                    onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                  >
                    {form.name ? (
                      <span className="text-fg-50">{form.name}</span>
                    ) : (
                      <span className="text-fg-60">
                        Search and select a model...
                      </span>
                    )}
                    <ChevronDown
                      className={`w-4 h-4 text-fg-60 transition-transform ${
                        isModelDropdownOpen ? "rotate-180" : ""
                      }`}
                    />
                  </div>

                  {/* Dropdown with search */}
                  {isModelDropdownOpen && (
                    <div className="absolute z-50 w-full mt-1 bg-bg-50 border border-bg-40 rounded-md shadow-lg max-h-80 overflow-hidden">
                      {/* Search input */}
                      <div className="p-2 border-b border-bg-40 sticky top-0 bg-bg-50">
                        <div className="relative">
                          <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-fg-60" />
                          <input
                            type="text"
                            className="w-full pl-8 pr-2 py-1.5 rounded bg-bg-60 text-fg-50 text-sm focus:outline-none focus:ring-1 focus:ring-ac-02"
                            placeholder="Search by ID, name, or provider..."
                            value={modelSearchQuery}
                            onChange={(e) => {
                              setModelSearchQuery(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                        </div>
                      </div>

                      {/* Model list */}
                      <div className="overflow-y-auto max-h-64">
                        {filteredModels.length > 0 ? (
                          filteredModels.map((model) => {
                            const isExisting = existingModelNames.includes(
                              model.id
                            );
                            return (
                              <div
                                key={model.id}
                                className={`px-3 py-2 border-b border-bg-40 last:border-b-0 ${
                                  isExisting
                                    ? "bg-bg-60 text-fg-60 cursor-not-allowed opacity-60"
                                    : "hover:bg-bg-40 cursor-pointer"
                                }`}
                                onClick={() => {
                                  if (!isExisting) {
                                    handleModelSelect(model.id);
                                  }
                                }}
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <div>
                                    <div className="text-sm font-medium text-fg-50">
                                      {model.id}
                                    </div>
                                    {model.name && (
                                      <div className="text-xs text-fg-60 mt-0.5">
                                        {model.name}
                                      </div>
                                    )}
                                  </div>
                                  {isExisting && (
                                    <span className="text-[10px] font-medium uppercase tracking-wide text-ac-02">
                                      Added
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          <div className="px-3 py-4 text-sm text-fg-60 text-center">
                            No models found matching "{modelSearchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Title
              </label>
              <input
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="title"
                value={form.title}
                onChange={handleChange}
                placeholder="e.g., GPT-4 Turbo"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Provider *
              </label>
              <select
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="provider"
                value={form.provider}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select a provider
                </option>
                {providerOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Type *
              </label>
              <select
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="type"
                value={form.type}
                onChange={handleChange}
                required
              >
                <option value="free">Free</option>
                <option value="paid">Paid</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                LLM Class *
              </label>
              <select
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="llmClass"
                value={form.llmClass}
                onChange={handleChange}
                required
              >
                <option value="low">Low</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Model Type *
              </label>
              <select
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="modelType"
                value={form.modelType}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select a model type
                </option>
                {modelTypeOptions.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt.charAt(0).toUpperCase() + opt.slice(1)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Source *
              </label>
              <select
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="source"
                value={form.source}
                onChange={handleChange}
                required
              >
                <option value="" disabled>
                  Select a source
                </option>
                {sourceOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Status
              </label>
              <select
                className="w-full py-2 px-3 rounded-md bg-bg-50 text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 text-sm"
                name="active"
                value={form.active}
                onChange={handleChange}
              >
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs font-medium text-fg-60 mb-1">
                <input
                  type="checkbox"
                  name="isAuto"
                  checked={form.isAuto || false}
                  onChange={handleChange}
                  className="w-4 h-4 rounded"
                />
                Set as Auto Model
              </label>
              <p className="text-xs text-fg-60 mt-1">
                When enabled, this model will be used for "Auto" selection and
                web search. Only one model can be auto at a time.
              </p>
            </div>

            {/* Enhance Prompt - only for image generation models */}
            {(String(form.modelType).toLowerCase() === "image" ||
              String(form.modelType).toLowerCase() === "image-edit") && (
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-xs font-medium text-fg-60 mb-1">
                  <input
                    type="checkbox"
                    name="enhancePrompt"
                    checked={!!form.enhancePrompt}
                    onChange={handleChange}
                    className="w-4 h-4 rounded"
                  />
                  Auto enhance / translate prompts to English
                </label>
                <p className="text-xs text-fg-60 mt-1">
                  When enabled, image prompts written in any language will be
                  optimized and converted to English before sending to the
                  model.
                </p>
              </div>
            )}
            <div className="col-span-2">
              <label className="block text-xs font-medium text-fg-60 mb-1">
                Support
              </label>
              <div className="flex gap-4 flex-wrap">
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name="support.imageIn"
                    checked={form.support.imageIn}
                    onChange={handleChange}
                  />{" "}
                  Image In
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name="support.imageOut"
                    checked={form.support.imageOut}
                    onChange={handleChange}
                  />{" "}
                  Image Out
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name="support.pdf"
                    checked={form.support.pdf}
                    onChange={handleChange}
                  />{" "}
                  PDF
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    name="support.search"
                    checked={form.support.search}
                    onChange={handleChange}
                  />{" "}
                  Search
                </label>
              </div>
            </div>
            <div className="col-span-2 flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-3 py-1.5 text-xs rounded bg-bg-50 text-fg-60 font-semibold hover:bg-bg-40"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-3 py-1.5 text-xs rounded bg-ac-02 text-white dark:text-black font-semibold hover:bg-ac-01"
              >
                Add Model
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default AddModelModal;
